
import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { useUser } from "@/contexts/user-context"

export type ViewMode = "admin" | "user" | "trainer" | "reception"

type ViewContextType = {
  viewMode: ViewMode
  hasUnreadNotifications: boolean
  setHasUnreadNotifications: (value: boolean) => void
  markNotificationsAsRead: () => void
}

const ViewContext = createContext<ViewContextType | undefined>(undefined)

export function ViewProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewMode] = useState<ViewMode>("admin")
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const { user } = useUser()

  // Set the view mode based on user type
  useEffect(() => {
    if (user?.userType) {
      // Map owner to admin view mode
      if (user.userType.toLowerCase() === "owner") {
        setViewMode("admin")
      } else {
        const userType = user.userType.toLowerCase() as ViewMode

        // Validate that the userType is a valid ViewMode
        if (["admin", "user", "trainer", "reception"].includes(userType)) {
          setViewMode(userType)
        } else {
          // Default to user view if userType is not recognized
          setViewMode("user")
        }
      }
    }
  }, [user])

  return (
    <ViewContext.Provider
      value={{
        viewMode,
        hasUnreadNotifications,
        setHasUnreadNotifications,
        markNotificationsAsRead: () => setHasUnreadNotifications(false),
      }}
    >
      {children}
    </ViewContext.Provider>
  )
}

export function useView() {
  const context = useContext(ViewContext)
  if (context === undefined) {
    throw new Error("useView must be used within a ViewProvider")
  }
  return context
}
